import React, { useEffect, useRef, useState } from "react";
import { Grid, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "./TextField";
import Button from "./Button";
import FreeSoloAutocomplete from "./FreeSoloAutocomplete";

/** Analog sensor types used by FMS report sensor selector (non-digital). */
export const REPORT_ANALOG_SENSORS = [
  { label: "Driver Unique ID", value: "driverUniqueId" },
  { label: "Passenger Unique ID", value: "passengerUniqueId" },
  { label: "Bin Unique ID", value: "binUniqueId" },
  { label: "Trailer Unique ID", value: "trailerUniqueId" },
  { label: "Ignition", value: "ignition" },
  { label: "Panic Button", value: "panicButton" },
  { label: "External Power", value: "externalPower" },
  { label: "Engine Status", value: "enginStatus" },
  { label: "Car Alarm", value: "carAlarm" },
  { label: "Doors", value: "doors" },
  { label: "Fuel", value: "fuel" },
  { label: "Temperature", value: "temperature" },
  { label: "RPM", value: "rpm" },
  { label: "Board Power", value: "boardPower" },
  { label: "Mileage", value: "mileage" },
  { label: "Speed", value: "speed" },
  { label: "Counter", value: "counter" },
  { label: "Average Fuel Consumption", value: "averagFuelConsumption" },
  { label: "Instant Fuel Consumption", value: "instantFuelConsumption" },
  { label: "Flow Meter", value: "flowMeter" },
  { label: "Humidity", value: "humidity" },
  { label: "Custom", value: "custom" },
  { label: "Weight", value: "weight" },
];

const EMPTY_ROW = { input: "", index: "" };

export const buildSensorString = (input, index) =>
  `${input || ""}${index != null ? String(index) : ""}`;

/**
 * Split a concatenated sensor string (e.g. "fuel1", "test3") back into type + index.
 * 1) Prefer known analog type prefixes (longest match first).
 * 2) Else treat a trailing digit run as the index ("test3" → type test, index 3).
 *    Requires at least one non-digit before the digits so bare "123" stays a type.
 */
export const parseSensorString = (sensor) => {
  if (!sensor || typeof sensor !== "string") {
    return { ...EMPTY_ROW };
  }
  const types = REPORT_ANALOG_SENSORS.map((s) => s.value).sort(
    (a, b) => b.length - a.length
  );
  for (const type of types) {
    if (sensor === type) {
      return { input: type, index: "" };
    }
    if (sensor.startsWith(type)) {
      const rest = sensor.slice(type.length);
      if (rest === "" || /^\d+$/.test(rest)) {
        return { input: type, index: rest };
      }
    }
  }
  const trailingDigits = sensor.match(/^(.*\D)(\d+)$/);
  if (trailingDigits) {
    return { input: trailingDigits[1], index: trailingDigits[2] };
  }
  return { input: sensor, index: "" };
};

export const sensorsToRows = (sensors) => {
  if (Array.isArray(sensors) && sensors.length) {
    return sensors.filter(Boolean).map(parseSensorString);
  }
  return [{ ...EMPTY_ROW }];
};

export const rowsToSensors = (rows) =>
  (rows || [])
    .map((row) => buildSensorString(row?.input, row?.index))
    .filter(Boolean);

const sensorsKey = (sensors) =>
  JSON.stringify(Array.isArray(sensors) ? sensors.filter(Boolean) : []);

/**
 * Multi-row sensor type + index selector for Measurement Sensor Report templates.
 * Persists only `sensors: string[]` (e.g. ["fuel1", "temperature"]).
 * Rows are held locally so empty draft rows (Add sensor) are not dropped.
 */
export default function ReportSensorSelector({
  value,
  onChange,
  translate,
  sensorOptions = REPORT_ANALOG_SENSORS,
}) {
  const t = translate || ((key) => key);
  const [rows, setRows] = useState(() => sensorsToRows(value));
  const lastEmittedKey = useRef(sensorsKey(value));

  useEffect(() => {
    const nextKey = sensorsKey(value);
    // Sync from parent only when saved sensors changed externally (load/edit),
    // not when we just emitted the same filled sensors while keeping empty rows.
    if (nextKey !== lastEmittedKey.current) {
      lastEmittedKey.current = nextKey;
      setRows(sensorsToRows(value));
    }
  }, [value]);

  const commit = (nextRows) => {
    setRows(nextRows);
    const sensors = rowsToSensors(nextRows);
    lastEmittedKey.current = sensorsKey(sensors);
    onChange(sensors);
  };

  const updateRow = (idx, field, fieldValue) => {
    commit(
      rows.map((row, i) => (i === idx ? { ...row, [field]: fieldValue } : row))
    );
  };

  const addRow = () => {
    commit([...rows, { ...EMPTY_ROW }]);
  };

  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    commit(next.length ? next : [{ ...EMPTY_ROW }]);
  };

  return (
    <div style={{ width: "100%" }}>
      <h4 className="tab-title" style={{ marginBottom: 8 }}>
        {t("sensors")}
      </h4>
      <Grid container spacing={1} alignItems="center" style={{ marginBottom: 4 }}>
        <Grid item xs={6} sm={5}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>{t("type")}</span>
        </Grid>
        <Grid item xs={4} sm={3}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>{t("index")}</span>
        </Grid>
        <Grid item xs={2} sm={2} />
      </Grid>
      {rows.map((row, idx) => (
        <Grid
          container
          spacing={1}
          alignItems="center"
          key={idx}
          style={{ marginBottom: 4 }}
        >
          <Grid item xs={6} sm={5}>
            <FreeSoloAutocomplete
              options={sensorOptions}
              value={row.input || ""}
              onChange={(val) => updateRow(idx, "input", val)}
              label={t("type")}
              placeholder={t("type")}
              fullWidth
            />
          </Grid>
          <Grid item xs={4} sm={3}>
            <TextField
              name={`sensorIndex-${idx}`}
              label={t("index")}
              type="number"
              placeholder={t("indexPlaceholder")}
              value={row.index || ""}
              onChange={(e) => updateRow(idx, "index", e.target.value)}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={2} sm={2}>
            {rows.length > 1 ? (
              <IconButton
                size="small"
                onClick={() => removeRow(idx)}
                aria-label={t("delete")}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Grid>
        </Grid>
      ))}
      <Button size="small" onClick={addRow} style={{ marginTop: 8 }}>
        {t("addSensor")}
      </Button>
    </div>
  );
}
